import type { AdminDogMedication } from '../../lib/api.js';

export interface MedicationListProps {
  readonly medications: readonly AdminDogMedication[];
  readonly now?: Date;
}

const FREQUENCY_LABEL: Record<string, string> = {
  daily: '1× / day',
  twice_daily: '2× / day',
  three_times_daily: '3× / day',
  weekly: 'Weekly',
  as_needed: 'As needed',
};

function formatFrequency(freq: string | null): string {
  if (!freq) return 'No schedule on file';
  return FREQUENCY_LABEL[freq] ?? freq.replace(/_/g, ' ');
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function isCurrentlyActive(med: AdminDogMedication, now: Date): boolean {
  if (!med.is_active) return false;
  if (med.end_date && new Date(med.end_date) < now) return false;
  return true;
}

export function MedicationList({
  medications,
  now = new Date(),
}: MedicationListProps): JSX.Element {
  if (medications.length === 0) {
    return (
      <p className="ferro-muted ferro-dog-meds__empty">
        No medications on file
      </p>
    );
  }

  return (
    <ul className="ferro-dog-meds" aria-label="Medications">
      {medications.map((med) => {
        const active = isCurrentlyActive(med, now);
        const start = formatDate(med.start_date);
        const end = formatDate(med.end_date);
        return (
          <li
            key={med.id}
            className={`ferro-dog-meds__item ferro-dog-meds__item--${
              active ? 'active' : 'inactive'
            }`}
            data-active={active}
          >
            <div className="ferro-dog-meds__head">
              <span className="ferro-dog-meds__name">{med.name}</span>
              {active ? (
                <span className="ferro-dog-meds__tag ferro-dog-meds__tag--active">
                  Active
                </span>
              ) : (
                <span className="ferro-dog-meds__tag ferro-dog-meds__tag--inactive">
                  Inactive
                </span>
              )}
            </div>
            <div className="ferro-dog-meds__meta">
              {med.dosage ? (
                <span className="ferro-dog-meds__dose">{med.dosage}</span>
              ) : null}
              <span className="ferro-dog-meds__freq">
                {formatFrequency(med.frequency)}
              </span>
              {start ? (
                <span className="ferro-dog-meds__range">
                  Since {start}
                  {end ? ` · ends ${end}` : ''}
                </span>
              ) : end ? (
                <span className="ferro-dog-meds__range">Ends {end}</span>
              ) : null}
            </div>
            {med.instructions ? (
              <p className="ferro-dog-meds__instructions">
                {med.instructions}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function hasActiveMedication(
  dog: { medications: readonly AdminDogMedication[] },
  now: Date = new Date(),
): boolean {
  return dog.medications.some((m) => isCurrentlyActive(m, now));
}
